import {
    ColorFinderImageWithContext,
    GenericUpload,
    ID,
    ImagePreviewType,
    ImageProcessingState,
    ImageWarnings,
    isUsable,
    UploadStateSummary,
} from "@nova/constants"
import { orthoImage, rgbDownscaledImagePath } from "@nova/util-s3"
import { createSelector } from "@reduxjs/toolkit"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    selectImageColorFinderFlightById,
    selectImageColorFinderFlights,
} from "features/entity-caching/flights/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import store from "integrations/redux/store"
import { get, memoize } from "nodash"
import {
    ColorFinderState,
    selectAllUploads,
    selectUploadsForSet as selectColorFinderUploadsForSet,
} from "plugins/ColorFinder/ImageColorFinder/ImageColorFinderSlice"
import { getFileNameFromImageId, UploadState } from "@nova/slice-multifile-upload"
import { imageColorfinderWarningProcessor } from "plugins/ColorFinder/ImageColorFinder/ImageColorfinderWarnings"
import { RootState } from "integrations/redux"

export const selectColorFinderImagesByFlightId = memoize((id: string) =>
    createSelector(
        [selectImageColorFinderFlightById(id), selectActiveWorkspaceId(), selectActivatedFireId],
        (flight, orgId, projectId) => {
            if (!orgId || !projectId) {
                return []
            }
            return flight?.images.map((image) => ({
                ...image,
                s3Key: orthoImage(orgId, projectId, flight.id, image.id),
                S3KeyLR: image.hasLowQuality
                    ? rgbDownscaledImagePath(orgId, projectId, flight.id, image.id)
                    : undefined,
                flightId: id,
                fileName: getFileNameFromImageId(image.id),
                state: ImageProcessingState.UploadDone,
            })) as ColorFinderImageWithContext[]
        }
    )
)

export const selectColorFinderImagesContextMap = createSelector(
    [selectImageColorFinderFlights],
    (flights) => {
        const state = store.getState()
        const imageMap = {} as Record<string, ColorFinderImageWithContext>
        flights.forEach((flight) => {
            selectColorFinderImagesByFlightId(flight.id)(state).forEach(
                (image) => (imageMap[image.id] = image)
            )
        })
        return imageMap
    }
)

export const selectColorFinderImageById = memoize((id: string) =>
    createSelector([selectAllUploads(), selectColorFinderImagesContextMap], (uploads, images) => {
        const upload = uploads[id]
        const cached = images[id]
        if (cached) {
            if (!upload) return cached
            const uploadError = get(upload, "error")
            return {
                ...cached,
                state: get(upload, "state"),
                ...(uploadError !== undefined ? { error: uploadError } : {}),
            }
        }
        return upload
    })
)

export const selectColorFinderImagesAndUploadsByFlightId = memoize((id: string) =>
    createSelector(
        [selectColorFinderImagesByFlightId(id), selectColorFinderUploadsForSet(id)],
        (images, uploads) => {
            const uploadById = new Map(uploads?.map((u) => [u.id, u]))
            const res = [] as (UploadState<ColorFinderState> | ColorFinderImageWithContext)[]
            images?.forEach((image) => {
                const activeUpload = uploadById.get(image.id)
                if (activeUpload) {
                    const cfUploadError = get(activeUpload, "error")
                    res.push({
                        ...image,
                        state: get(activeUpload, "state"),
                        ...(cfUploadError !== undefined ? { error: cfUploadError } : {}),
                    } as unknown as ColorFinderImageWithContext)
                    uploadById.delete(image.id)
                } else {
                    res.push(image)
                }
            })
            uploadById.forEach((upload) => res.push(upload))
            return res
        }
    )
)

export const selecteColorfinderImagesAndUploadsWithContextByFlightId = memoize((id: string) =>
    createSelector(
        [selectColorFinderImagesByFlightId(id), selectColorFinderUploadsForSet(id)],
        (images, uploads) => {
            let imageColors: Record<ID, string> = {}
            if (
                uploads?.every(
                    (upload) =>
                        (upload as GenericUpload).state === ImageProcessingState.ProcessingDone
                )
            ) {
                imageColors = imageColorfinderWarningProcessor(id, uploads as ColorFinderState[])
            }
            const uploadById = new Map(uploads?.map((u) => [u.id, u]))
            const res = [] as (UploadState<ColorFinderState> | ColorFinderImageWithContext)[]
            images?.forEach((image) => {
                const activeUpload = uploadById.get(image.id)
                if (activeUpload) {
                    const error = get(activeUpload, "error")
                    res.push({
                        ...image,
                        state: get(activeUpload, "state"),
                        ...(error !== undefined ? { error: error } : {}),
                    } as unknown as ColorFinderImageWithContext)
                    uploadById.delete(image.id)
                } else {
                    res.push(image)
                }
            })
            uploadById.forEach((upload) => res.push(upload))
            return res.map((upload) => ({
                ...upload,
                warningColor: imageColors[upload.id],
                imagePreviewType: ImagePreviewType.Colorfinder,
            }))
        }
    )
)

export const selectSummaryFromImageColorfinderUploads = memoize((id: string) =>
    createSelector([selectColorFinderImagesAndUploadsByFlightId(id)], (uploads) => {
        const summary: UploadStateSummary = {
            stateCount: {} as Record<ImageProcessingState, number>,
            uploadCount: uploads?.length || 0,
            errorCount: 0,
            warnings: {} as ImageWarnings,
        }
        uploads?.forEach((upload) => {
            const usable = isUsable(upload)
            if (!usable) return summary.errorCount++
            const state = "state" in usable ? usable.state : ImageProcessingState.UploadDone
            summary.stateCount[state] = ++summary.stateCount[state] || 1
        })
        return summary
    })
)

export const selectImageColorfinderUploadSize = memoize((id: string) =>
    createSelector([selectColorFinderImagesAndUploadsByFlightId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const size = get(upload, "size", 0)
            totalSize += size
        })
        return totalSize
    })
)

export const selectImageColorfinderFinishedUploadSize = memoize((id: string) =>
    createSelector([selectColorFinderImagesAndUploadsByFlightId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const state = get(upload, "state")
            const size = get(upload, "size", 0)
            if (state !== ImageProcessingState.UploadDone) return
            totalSize += size
        })
        return totalSize
    })
)

export const selectIsImageColorfinderFlightUploading = memoize((id: string) =>
    createSelector([selectSummaryFromImageColorfinderUploads(id)], (summary) => {
        return (
            !!summary.stateCount?.[ImageProcessingState.PendingUpload] ||
            !!summary.stateCount?.[ImageProcessingState.Uploading]
        )
    })
)

const root = (state: RootState) => state.project.data.imageColorFinderFlights

export const selectAllImageColorfinderFlights = (state: RootState) => root(state)
